import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const refundRules = [
  {
    days: '3일 전 이상',
    penalty: '위약금 없음',
    penaltyRate: 0,
    desc: '체크인 3일 전 또는 그 이전에 예약취소할 때에는 위약금은 없습니다.',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: 'ri-checkbox-circle-line',
    iconColor: 'text-emerald-500',
  },
  {
    days: '2일 전',
    penalty: '요금의 50%',
    penaltyRate: 50,
    desc: '체크인 2일 전 예약취소시 위약금은 50%입니다.',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'ri-error-warning-line',
    iconColor: 'text-amber-500',
  },
  {
    days: '1일 전 / 당일 노쇼',
    penalty: '요금의 100%',
    penaltyRate: 100,
    desc: '하루 전 또는 체크인을 안하실 경우에는 요금총액의 100%를 위약금으로 청구합니다.',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'ri-close-circle-line',
    iconColor: 'text-red-500',
  },
];

const RefundPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = '예약 환불규정 | 영빈호텔';
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    metaRobots.id = 'meta-robots-refund';
    document.head.appendChild(metaRobots);
    return () => {
      document.title = '영빈호텔 | YOUNGBIN HOTEL';
      const el = document.getElementById('meta-robots-refund');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Sub Header */}
      <div className="pt-20 bg-stone-800">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <p className="text-amber-400 text-xs tracking-[0.3em] uppercase mb-2">CANCELLATION POLICY</p>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            예약 환불규정
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-20">

        {/* 타이틀 */}
        <div className="text-center mb-14">
          <img
            src="https://storage.readdy-site.link/project_files/f1a4733f-f142-44b4-9a4b-319ee3c3a442/cf5d4cea-d623-4733-86a8-803e415ede74_20161028_-yeong-bin-ho--ro-go_ol1574127236.png?v=7dd96618540c35f269119a9b3765e2fd"
            alt="YOUNGBIN HOTEL"
            className="h-12 object-contain mx-auto mb-2"
          />
          <p className="text-stone-400 text-xs tracking-widest">in Dongdaemun</p>
        </div>

        {/* 규정 요약 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
          {refundRules.map((rule) => (
            <div key={rule.days} className={`rounded-xl border ${rule.border} ${rule.bg} p-6 flex flex-col items-center text-center`}>
              <div className={`w-14 h-14 flex items-center justify-center rounded-full bg-white mb-4`}>
                <i className={`${rule.icon} text-2xl ${rule.iconColor}`} />
              </div>
              <p className="text-xs font-semibold text-stone-500 tracking-widest uppercase mb-1">취소 시점</p>
              <p className="text-base font-bold text-gray-900 mb-3">{rule.days}</p>
              <div className={`text-2xl font-bold ${rule.color} mb-1`}>{rule.penalty}</div>
            </div>
          ))}
        </div>

        {/* 상세 규정 리스트 */}
        <div className="bg-stone-50 rounded-xl border border-stone-200 p-8 mb-14">
          <h2 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-file-list-3-line text-amber-700" />
            </div>
            상세 환불 규정
          </h2>
          <ul className="flex flex-col gap-5">
            {refundRules.map((rule) => (
              <li key={rule.days} className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className={`${rule.icon} text-base ${rule.iconColor}`} />
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{rule.desc}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* 일러스트 + 안내 문구 */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-10 mb-14">
          <div className="w-40 h-52 overflow-hidden rounded-xl flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/3b373cb2e7e9f39afe83e19db01d9acc.jpg"
              alt="호텔 직원 남성"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="flex-1 max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              고객님의 소중한 예약을<br />안전하게 지켜드립니다
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              예약 취소 및 변경은 체크인 3일 전까지 위약금 없이 가능합니다.<br />
              부득이한 사정이 있으신 경우 사전에 연락 주시면 최대한 도움을 드리겠습니다.
            </p>
            <a
              href="tel:0222771141"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-700 text-white rounded-full text-sm font-semibold hover:bg-amber-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line" />
              </div>
              02-2277-1141 문의하기
            </a>
          </div>
          <div className="w-40 h-52 overflow-hidden rounded-xl flex-shrink-0">
            <img
              src="https://readdy.ai/api/search-image?query=friendly%20female%20hotel%20concierge%20staff%20in%20elegant%20black%20business%20suit%20with%20colorful%20scarf%2C%20flat%20illustration%20style%2C%20simple%20clean%20white%20background%2C%20welcoming%20gesture%20with%20open%20hand%2C%20professional%20hospitality%20worker%2C%20cartoon%20character%20design%2C%20minimalist%20vector%20art%20style&width=320&height=420&seq=refund-staff-female-01&orientation=portrait"
              alt="호텔 직원 여성"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* 추가 안내 */}
        <div className="border border-stone-200 rounded-xl p-6 mb-12">
          <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-information-line text-amber-600" />
            </div>
            유의사항
          </h4>
          <ul className="flex flex-col gap-2.5">
            {[
              '예약 취소는 전화 또는 이메일로 접수 가능합니다.',
              '성수기(7~8월, 설·추석 연휴)에는 별도의 취소 규정이 적용될 수 있습니다.',
              '단체 예약(5실 이상)의 경우 별도 취소 규정이 적용됩니다.',
              '위약금은 예약 시 결제하신 카드로 자동 청구됩니다.',
            ].map((note) => (
              <li key={note} className="flex items-start gap-2 text-sm text-gray-600">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-arrow-right-s-line text-amber-500" />
                </div>
                {note}
              </li>
            ))}
          </ul>
        </div>

        {/* Back */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-3 border border-stone-300 text-gray-700 rounded-full text-sm hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </div>
            메인으로 돌아가기
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RefundPage;
